/**
 * ComplaintsPanel
 *
 * Admin → "Complaints" tab. Shows every logged complaint. Filters by
 * status + attended-by + free-text search. Inline editing via the same
 * ComplaintDialog the dashboard opens — mirrors the "open the same
 * dialog instead" pattern that lives elsewhere in the admin area.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Search, AlertCircle, RefreshCw, Pencil, Camera, X, Loader2, Eye, Trash2 } from 'lucide-react';
import { api, API, useAuth } from '../../lib/store';
import axios from 'axios';

const STATUSES = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'closed',      label: 'Closed' },
];

const STATUS_LABEL = { pending: 'Pending', in_progress: 'In Progress', closed: 'Closed' };
const STATUS_CHIP = {
  pending:     'bg-[#F9A825]/10 border-[#F9A825]/40 text-[#8B6914]',
  in_progress: 'bg-[#2F6B8A]/10 border-[#2F6B8A]/40 text-[#1D4E68]',
  closed:      'bg-[#2E7D32]/10 border-[#2E7D32]/40 text-[#1B5E20]',
};

const MAX_SITE_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export default function ComplaintsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [complaints, setComplaints] = useState([]);
  const [total, setTotal] = useState(0);
  const [salespeople, setSalespeople] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [attendedByFilter, setAttendedByFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  // Complaint ID for display — existing records have no complaint_number,
  // so we fall back to the first 8 chars of the internal uuid.
  const displayId = (c) => c?.complaint_number || (c?.id ? c.id.slice(0, 8) : '—');

  // Destructive-action confirmation — mirrors the window.confirm pattern
  // used by LeadRegisterPanel.jsx (~L99 handleDeleteLead).
  const handleDeleteComplaint = async (c) => {
    const label = c.complaint_number || (c.id ? c.id.slice(0, 8) : c.customer_name);
    if (!window.confirm(`Delete complaint ${label} for ${c.customer_name}? This cannot be undone.`)) return;
    try {
      await api.deleteComplaint(c.id);
      toast.success('Complaint deleted');
      fetchComplaints();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete complaint');
    }
  };

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (attendedByFilter !== 'all') params.attended_by = attendedByFilter;
      const res = await api.getComplaints(params);
      setComplaints(res.data?.complaints || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.getSalespeople()
      .then((res) => setSalespeople(res.data?.salespeople || []))
      .catch(() => setSalespeople([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) fetchComplaints(); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, attendedByFilter]);

  const onSearchKey = (e) => {
    if (e.key === 'Enter') fetchComplaints();
  };

  return (
    <div className="space-y-4" data-testid="admin-complaints-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-[#1A1C16] flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-[#B03A3A]" /> Complaints
            <Badge variant="outline" className="ml-1 text-[10px] font-mono-data">{total}</Badge>
          </h2>
          <p className="text-[11px] text-[#8F9485] mt-0.5">
            Every complaint logged from the dashboard. Update the status once resolved — no records are deleted.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchComplaints} disabled={loading} data-testid="complaints-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="cx-search" className="text-xs">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#8F9485] absolute left-2 top-1/2 -translate-y-1/2" />
            <Input
              id="cx-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Customer, mobile, staff, notes…"
              className="pl-7"
              data-testid="complaints-search"
            />
          </div>
        </div>
        <div className="w-40">
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="complaints-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Label className="text-xs">Attended By</Label>
          <Select value={attendedByFilter} onValueChange={setAttendedByFilter}>
            <SelectTrigger data-testid="complaints-filter-attended"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              {salespeople.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={fetchComplaints} data-testid="complaints-apply-btn">Apply</Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-[#E2E6D9] rounded-md bg-white">
        <table className="w-full text-sm" data-testid="complaints-table">
          <thead className="bg-[#F2F4EF] text-[#5C6150] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Complaint ID</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Mobile</th>
              <th className="text-left px-3 py-2">Attended By</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">Action Taken</th>
              <th className="text-left px-3 py-2">Logged By</th>
              <th className="text-left px-3 py-2">Photos</th>
              <th className="text-right px-3 py-2">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center text-[#8F9485] py-6">Loading…</td></tr>
            )}
            {!loading && complaints.length === 0 && (
              <tr><td colSpan={11} className="text-center text-[#8F9485] py-6">No complaints found.</td></tr>
            )}
            {!loading && complaints.map((c) => (
              <tr key={c.id} className="border-t border-[#E2E6D9]" data-testid={`complaints-row-${c.id}`}>
                <td className="px-3 py-2 whitespace-nowrap font-mono-data text-[11px] text-[#5C6150]">{displayId(c)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-[#5C6150]">{fmtDate(c.created_at)}</td>
                <td className="px-3 py-2 font-medium text-[#1A1C16]">{c.customer_name}</td>
                <td className="px-3 py-2 whitespace-nowrap text-[#5C6150]">{c.mobile || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-[#5C6150]">{c.attended_by_name || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_CHIP[c.status] || STATUS_CHIP.pending}`}>
                    {STATUS_LABEL[c.status] || 'Pending'}
                  </span>
                </td>
                <td className="px-3 py-2 text-[#5C6150] max-w-[220px] truncate" title={c.description}>{c.description || '—'}</td>
                <td className="px-3 py-2 text-[#5C6150] max-w-[220px] truncate" title={c.action_taken}>{c.action_taken || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-[#5C6150]">{c.created_by_name || '—'}</td>
                <td className="px-3 py-2">
                  {(c.images || []).length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#5C6150]">
                      <Camera className="w-3 h-3" /> {c.images.length}
                    </span>
                  ) : (
                    <span className="text-[11px] text-[#B7BFA8]">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewing(c)}
                    data-testid={`complaints-view-${c.id}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(c)}
                    data-testid={`complaints-edit-${c.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteComplaint(c)}
                      className="text-[#B03A3A] hover:text-[#8F2E2E] hover:bg-[#B03A3A]/10"
                      data-testid={`complaints-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EditComplaintDialog
        complaint={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchComplaints(); }}
        salespeople={salespeople}
      />

      <ViewComplaintDialog
        complaint={viewing}
        onClose={() => setViewing(null)}
        displayId={displayId}
      />
    </div>
  );
}


// ─── View dialog — read-only detail. Simplest possible Dialog with
// labelled rows, keeping in style with the edit dialog shell above.
function ViewComplaintDialog({ complaint, onClose, displayId }) {
  const open = !!complaint;
  if (!complaint) return null;
  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
      <span className="text-[11px] uppercase tracking-wide text-[#8F9485] pt-0.5">{label}</span>
      <span className="text-[#1A1C16] break-words whitespace-pre-wrap">{children ?? '—'}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col" data-testid="complaint-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-[#B03A3A]" /> Complaint Details
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          <Row label="Complaint ID"><span className="font-mono-data text-[12px]">{displayId(complaint)}</span></Row>
          <Row label="Date">{fmtDate(complaint.created_at)}</Row>
          <Row label="Customer">{complaint.customer_name}</Row>
          <Row label="Mobile">{complaint.mobile || '—'}</Row>
          <Row label="Description">
            <span data-testid="complaint-view-description" className="block leading-relaxed">
              {complaint.description || '—'}
            </span>
          </Row>
          <Row label="Attended By">{complaint.attended_by_name || '—'}</Row>
          <Row label="Status">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_CHIP[complaint.status] || STATUS_CHIP.pending}`}>
              {STATUS_LABEL[complaint.status] || 'Pending'}
            </span>
          </Row>
          <Row label="Action Taken">{complaint.action_taken || '—'}</Row>
          <Row label="Remarks">{complaint.remarks || '—'}</Row>
          <Row label="Logged By">{complaint.created_by_name || '—'}</Row>
          <Row label="Photos">
            {(complaint.images || []).length === 0 ? '—' : (
              <div className="flex flex-wrap gap-2">
                {complaint.images.map((filename) => (
                  <div key={filename} className="w-16 h-16 rounded-md overflow-hidden border border-[#E2E6D9] bg-[#F9FAF7]">
                    <img src={`${API}/uploads/${filename}`} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </Row>
        </div>
        <DialogFooter className="flex-shrink-0 pt-3 border-t border-[#E2E6D9]">
          <Button type="button" variant="outline" onClick={onClose} data-testid="complaint-view-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Edit dialog — matches the create-dialog shell used elsewhere ────
function EditComplaintDialog({ complaint, onClose, onSaved, salespeople }) {
  const open = !!complaint;
  const [form, setForm] = useState({
    customer_name: '', mobile: '', description: '', attended_by_id: '', attended_by_name: '',
    status: 'pending', action_taken: '', remarks: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);

  useEffect(() => {
    if (complaint) {
      setForm({
        customer_name:    complaint.customer_name    || '',
        mobile:           complaint.mobile           || '',
        description:      complaint.description      || '',
        attended_by_id:   complaint.attended_by_id   || '',
        attended_by_name: complaint.attended_by_name || '',
        status:           complaint.status           || 'pending',
        action_taken:     complaint.action_taken     || '',
        remarks:          complaint.remarks          || '',
      });
      setPendingPhotos([]);
      setPendingPreviews((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
    }
  }, [complaint]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onMobile = (e) => setForm((f) => ({ ...f, mobile: (e.target.value || '').replace(/\D/g, '') }));

  const handleAttendedBy = (id) => {
    const sp = salespeople.find((x) => x.id === id);
    setForm((f) => ({ ...f, attended_by_id: id, attended_by_name: sp?.name || '' }));
  };

  const handlePickPhotos = (e) => {
    const chosen = Array.from(e.target.files || []);
    const existingCount = (complaint?.images || []).length;
    const remaining = MAX_SITE_IMAGES - existingCount - pendingPhotos.length;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_SITE_IMAGES} photos per complaint`);
      e.target.value = '';
      return;
    }
    const accepted = [], previews = [];
    for (const f of chosen.slice(0, remaining)) {
      if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not an image`); continue; }
      if (f.size > MAX_IMAGE_BYTES) { toast.error(`${f.name}: exceeds 5 MB`); continue; }
      accepted.push(f);
      previews.push(URL.createObjectURL(f));
    }
    setPendingPhotos((prev) => [...prev, ...accepted]);
    setPendingPreviews((prev) => [...prev, ...previews]);
    e.target.value = '';
  };

  const removePending = (idx) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleDeleteExisting = async (filename) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await api.deleteComplaintImage(complaint.id, filename);
      toast.success('Photo removed');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleSave = async () => {
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    setSubmitting(true);
    try {
      await api.updateComplaint(complaint.id, form);
      if (pendingPhotos.length > 0) {
        try {
          const fd = new FormData();
          pendingPhotos.forEach((f) => fd.append('files', f, f.name));
          const token = localStorage.getItem('heda_token') || '';
          await axios.post(`${API}/complaints/${complaint.id}/images`, fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
        } catch (upErr) {
          toast.error(upErr.response?.data?.detail || 'Photos upload failed — changes saved');
        }
      }
      toast.success('Complaint updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update complaint');
    } finally {
      setSubmitting(false);
    }
  };

  if (!complaint) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col" data-testid="complaint-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-[#B03A3A]" /> Edit Complaint
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3 [&_input]:min-h-11 sm:[&_input]:min-h-10">
          <div>
            <Label className="text-xs">Customer Name <span className="text-red-500">*</span></Label>
            <Input value={form.customer_name} onChange={set('customer_name')} required data-testid="complaint-edit-name" />
          </div>

          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={4} value={form.description} onChange={set('description')} data-testid="complaint-edit-description" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Mobile</Label>
              <Input type="tel" inputMode="numeric" value={form.mobile} onChange={onMobile} data-testid="complaint-edit-mobile" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={setSel('status')}>
                <SelectTrigger data-testid="complaint-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Attended By</Label>
            <Select value={form.attended_by_id || undefined} onValueChange={handleAttendedBy}>
              <SelectTrigger data-testid="complaint-edit-attended"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {salespeople.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Action Taken</Label>
            <Textarea rows={2} value={form.action_taken} onChange={set('action_taken')} data-testid="complaint-edit-action" />
          </div>

          <div>
            <Label className="text-xs">Remarks</Label>
            <Textarea rows={2} value={form.remarks} onChange={set('remarks')} data-testid="complaint-edit-remarks" />
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-[#B03A3A]" /> Photos
            </Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(complaint.images || []).map((filename) => (
                <div key={filename} className="relative w-16 h-16 rounded-md overflow-hidden border border-[#E2E6D9] bg-[#F9FAF7]">
                  <img src={`${API}/uploads/${filename}`} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => handleDeleteExisting(filename)}
                    className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {pendingPreviews.map((url, idx) => (
                <div key={url} className="relative w-16 h-16 rounded-md overflow-hidden border border-[#E2E6D9] bg-[#F9FAF7]">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePending(idx)}
                    className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {((complaint.images || []).length + pendingPhotos.length) < MAX_SITE_IMAGES && (
                <label className="w-16 h-16 rounded-md border border-dashed border-[#B7BFA8] bg-[#F9FAF7] flex flex-col items-center justify-center cursor-pointer hover:bg-[#F2F4EF]">
                  <Camera className="w-4 h-4 text-[#5C6150]" />
                  <span className="text-[9px] text-[#5C6150] mt-0.5">Add</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePickPhotos} />
                </label>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-[#E2E6D9] gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting} data-testid="complaint-edit-cancel">Cancel</Button>
          <Button type="button" onClick={handleSave} className="bg-[#B03A3A] hover:bg-[#8F2E2E] text-white" disabled={submitting} data-testid="complaint-edit-save">
            {submitting ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Saving…</> : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
